/******************************************************************************
==============================================================================
BabiGO

useNotifications

Hook React permettant de gérer les notifications
de l'utilisateur connecté.

==============================================================================
*/

import {

    useCallback,

    useEffect,

    useState,

    useRef,

} from "react";

import useAuth from "./useAuth";

import notificationRepository

    from "../database/repositories/notificationRepository";

export default function useNotifications() {

    /* =======================================================================
       AUTH
    ======================================================================= */

    const {

        user,

    } = useAuth();

    /* =======================================================================
       ETATS
    ======================================================================= */

    const [

        notifications,

        setNotifications,

    ] = useState([]);

    const [

        unreadCount,

        setUnreadCount,

    ] = useState(0);

    const [

        error,

        setError,

    ] = useState(null);

    const [

        loading,

        setLoading,

    ] = useState(true);


    const refreshRef = useRef();

    /* =======================================================================
    ECOUTE TEMPS REEL
    ======================================================================= */

    useEffect(() => {

        if (!user?.uid) {

            setNotifications([]);

            setUnreadCount(0);

            setError(null);

            setLoading(false);

            return;

        }

        setLoading(true);

        setError(null);

        const unsubscribe =

            notificationRepository.subscribe(

                user.uid,

                (result) => {

                    setNotifications(

                        result.notifications

                    );

                    setUnreadCount(

                        result.unreadCount

                    );

                    setLoading(false);

                },

                (error) => {

                    console.error(error);

                    setError(error);

                    setLoading(false);

                }

            );

        return () => {

            if (

                typeof unsubscribe === "function"

            ) {

                unsubscribe();

            }

        };

    }, [

        user?.uid,

    ]);

        /* =======================================================================
       MARQUER COMME LUE
    ======================================================================= */

    const markAsRead = useCallback(

        async (notificationId) => {

            setError(null);

            /*
             * Mise à jour optimiste
             */

            setNotifications((previous) =>

                previous.map((notification) =>

                    notification.id === notificationId

                        ? {

                            ...notification,

                            isRead: true,

                        }

                        : notification

                )

            );

            setUnreadCount((previous) =>

                Math.max(0, previous - 1)

            );

            try {

                await notificationRepository.markAsRead(

                    notificationId

                );

            } catch (error) {

                console.error(error);
                
                setError(error);

            }

        },

        [],

    );

    /* =======================================================================
       MARQUER COMME NON LUE
    ======================================================================= */

    const markAsUnread = useCallback(

        async (notificationId) => {

            setError(null);

            setNotifications((previous) =>

                previous.map((notification) =>

                    notification.id === notificationId

                        ? {

                            ...notification,

                            isRead: false,

                        }

                        : notification

                )

            );

            setUnreadCount((previous) =>

                previous + 1

            );

            try {

                await notificationRepository.markAsUnread(

                    notificationId

                );

            } catch (error) {

                console.error(error);
                
                setError(error);

            }

        },

        [],

    );

    /* =======================================================================
       TOUT MARQUER COMME LU
    ======================================================================= */

    const markAllAsRead = useCallback(

        async () => {

            setError(null);

            if (!user?.uid) {

                return;

            }

            setNotifications((previous) =>

                previous.map((notification) => ({

                    ...notification,

                    isRead: true,

                }))

            );

            setUnreadCount(0);

            try {

                await notificationRepository.markAllAsRead(

                    user.uid

                );

            } catch (error) {

                console.error(error);

                setError(error);

            }

        },

        [

            user?.uid,

        ],

    );

    /* =======================================================================
    RAFRAICHISSEMENT
    ======================================================================= */

    const refresh = useCallback(

        async () => {

            if (!user?.uid) {

                return;

            }

            setLoading(true);

            setError(null);

            try {

                const result =

                    await notificationRepository.getAllNotifications(

                        user.uid

                    );

                setNotifications(

                    result.notifications

                );

                setUnreadCount(

                    result.unreadCount

                );

            } catch (error) {

                console.error(error);

                setError(error);

            } finally {

                setLoading(false);

            }

        },

        [

            user?.uid,

        ],

    );

    useEffect(() => {

        refreshRef.current = refresh;

    }, [refresh]);

    /* =======================================================================
       ARCHIVER
    ======================================================================= */

    const archive = useCallback(

        async (notificationId) => {
            
            setError(null);

            const previousNotifications = notifications;

            setNotifications(

                previous =>

                    previous.filter(

                        notification =>

                            notification.id !== notificationId

                    )

            );

            try {

                await notificationRepository.archive(

                    notificationId

                );

                await refreshRef.current();

            } catch (error) {

                console.error(error);

                setError(error);

                setNotifications(

                    previousNotifications

                );

            }

        },

        [

            notifications,

        ],

    );

    /* =======================================================================
       RESTAURER
    ======================================================================= */

    const restore = useCallback(

        async (notificationId) => {
            
            setError(null);

            try {

                await notificationRepository.restore(

                    notificationId

                );

                await refresh();

            } catch (error) {

                console.error(error);

                setError(error);

            }

        },

        [

            refresh,

        ],

    );

    /* =======================================================================
       SUPPRESSION
    ======================================================================= */

    const deleteNotification = useCallback(

        async (notificationId) => {
            
            setError(null);

            const previousNotifications = notifications;

            setNotifications(

                previous =>

                    previous.filter(

                        notification =>

                            notification.id !== notificationId

                    )

            );

            try {

                await notificationRepository.delete(

                    notificationId

                );

                await refreshRef.current();

            } catch (error) {

                console.error(error);

                setError(error);

                setNotifications(

                    previousNotifications

                );

            }

        },

        [

            notifications,

        ],

    );

    /* =======================================================================
    ETATS DERIVES
    ======================================================================= */

    const totalNotifications =

        notifications.length;

    const unreadNotifications =

        notifications.filter(

            notification =>

                notification.isRead !== true

        );

    const archivedNotifications =

        notifications.filter(

            notification =>

                notification.isArchived === true

        );

    const activeNotifications =

        notifications.filter(

            notification =>

                notification.isArchived !== true

        );

    const unreadCountComputed =

        unreadNotifications.length;

    const hasNotifications =

        totalNotifications > 0;

    const hasUnreadNotifications =

        unreadCountComputed > 0;

    const isEmpty =

        totalNotifications === 0;

    /* =======================================================================
    EXPORT
    ======================================================================= */

    return {

        /*
        ===================================================================
        ETATS
        ===================================================================
        */

        notifications,

        activeNotifications,

        archivedNotifications,

        unreadNotifications,

        unreadCount,

        totalNotifications,

        loading,

        error,

        isEmpty,

        hasNotifications,

        hasUnreadNotifications,

        /*
        ===================================================================
        ACTIONS
        ===================================================================
        */

        refresh,

        markAsRead,

        markAsUnread,

        markAllAsRead,

        archive,

        restore,

        deleteNotification,

    };

}